import React, { HTMLProps } from "react";
import { OtpInput } from "@design-system/inputs";
import { useForm, Controller } from "react-hook-form";
import { Form } from "./components/Form";
import { useTranslation } from "react-i18next";
import { Button, FormErrorMessage } from "@design-system";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  DEFAULT_VALUES,
  IConfirmAccountFormFields,
  ConfirmAccountFormValidationSchema
} from "./helpers";
import { translations } from 'translations';

export interface IConfirmAccountFormProps
  extends Omit<HTMLProps<HTMLElement>, 'onSubmit'> {
  /**
   * Custom CSS tailwind styles.
   */
  className?: string;
  /**
   * Form default values.
   */
  defaultValues?: IConfirmAccountFormFields;
  /**
   * On submit action handler.
   */
  onSubmit: (v: IConfirmAccountFormFields) => void;
  /**
   * White label's primary color.
   */
  primaryColor: string;
}

export const ConfirmAccountForm: React.FC<IConfirmAccountFormProps> = (
  props
): JSX.Element => {
  const {
    className,
    defaultValues = DEFAULT_VALUES,
    onSubmit,
    primaryColor,
    ...rest
  } = props;

  const {
    control,
    formState,
    handleSubmit
  } = useForm<IConfirmAccountFormFields>({
    defaultValues: defaultValues,
    mode: 'all',
    resolver: yupResolver(ConfirmAccountFormValidationSchema),
  });

  const { t } = useTranslation();

  return (
    <Form className={className} onSubmit={handleSubmit(onSubmit)} {...rest}>
      <div className="mb-8">
        <Controller
          control={control}
          name="code"
          render={({ field, fieldState }) => (
            <div className="flex flex-col items-center gap-4">
              <OtpInput
                value={field.value}
                onChange={field.onChange}
                onComplete={() => handleSubmit(onSubmit)().catch(() => {})}
                primaryColor={primaryColor}
                isDisabled={formState.isSubmitting}
                autoFocus
              />
              {fieldState.error && (
                <FormErrorMessage className="mt-2 text-center">
                  {t(fieldState.error.message as string)}
                </FormErrorMessage>
              )}
            </div>
          )}
        />
      </div>

      <Button
        className="w-[250px] md:w-[280px]"
        color={primaryColor}
        isDisabled={formState?.isSubmitting || !formState?.isValid}
        isLoading={formState?.isSubmitting}
        size="md"
        type="submit">
        {t(translations.COMMON.VALIDATE)}
      </Button>
    </Form>
  )
}
